import type {
  RunState,
  LineStatus,
  ExportStatus,
  DisbursementItemStatus,
  DisbursementBatchStatus,
  RetryClass,
  ComponentKind,
  AlertSeverity,
  AlertStatus,
  AlertCategory,
  FilingStatus,
  FilingType,
} from '../types';

// Maps a domain status to a Badge `variant` (see ui/badge.tsx) + a human label.
// Centralised so every screen shows the same chip for the same state.

type Variant =
  | 'default'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'secondary'
  | 'outline'
  | 'purple'
  | 'teal';

export interface StatusMeta {
  label: string;
  variant: Variant;
}

/** Badge for a run line that was NOT calculated. A calculated line has no
 *  status chip — its figures are the status. */
export const LINE_STATUS_META: Record<Exclude<LineStatus, 'calculated'>, StatusMeta> = {
  error: { label: 'Errored', variant: 'danger' },
  excluded: { label: 'Excluded', variant: 'warning' },
  pending: { label: 'Not calculated', variant: 'info' },
};

export const RUN_STATE_META: Record<RunState, StatusMeta> = {
  draft: { label: 'Draft', variant: 'secondary' },
  validated: { label: 'Validated', variant: 'info' },
  approved: { label: 'Approved', variant: 'warning' },
  locked: { label: 'Locked', variant: 'default' },
  paid: { label: 'Paid', variant: 'success' },
  cancelled: { label: 'Cancelled', variant: 'danger' },
};

/** Ordered lifecycle for progress affordances. */
// Cancelled is absent on purpose: it is where a run STOPS, not a stage it passes
// through, and putting it in the order would draw it as progress.
export const RUN_STATE_ORDER: RunState[] = ['draft', 'validated', 'approved', 'locked', 'paid'];

export const EXPORT_STATUS_META: Record<ExportStatus, StatusMeta> = {
  requested: { label: 'Requested', variant: 'secondary' },
  generating: { label: 'Generating', variant: 'info' },
  generated: { label: 'Generated', variant: 'success' },
  failed: { label: 'Failed', variant: 'danger' },
  approved: { label: 'Approved', variant: 'warning' },
  submitted: { label: 'Submitted', variant: 'default' },
};

export const DISBURSEMENT_ITEM_META: Record<DisbursementItemStatus, StatusMeta> = {
  pending: { label: 'Pending', variant: 'secondary' },
  submitted: { label: 'Submitted', variant: 'info' },
  unknown: { label: 'Outcome being confirmed', variant: 'warning' },
  paid: { label: 'Paid', variant: 'success' },
  failed: { label: 'Failed', variant: 'danger' },
  reversed: { label: 'Reversed', variant: 'warning' },
  reconciliation_required: { label: 'Needs reconciliation', variant: 'warning' },
};

/** Humanized failure classifications (shown where a Retry isn't offered). */
export const RETRY_CLASS_LABEL: Record<RetryClass, string> = {
  safe_to_retry: 'Safe to retry',
  retry_with_status_check: 'Retry with status check',
  do_not_retry: 'Do not retry',
  manual_review_required: 'Manual review required',
};

export const DISBURSEMENT_BATCH_META: Record<DisbursementBatchStatus, StatusMeta> = {
  prepared: { label: 'Prepared', variant: 'secondary' },
  submitting: { label: 'Releasing…', variant: 'info' },
  submitted: { label: 'Submitted', variant: 'info' },
  completed: { label: 'Completed', variant: 'success' },
  failed: { label: 'Failed', variant: 'danger' },
};

// Loan status pills moved to src/features/credit (applicationPill/facilityPill)
// with the credit-service cutover.

// --- Pre-Payroll Control Center chips ---------------------------------------

export const ALERT_SEVERITY_META: Record<AlertSeverity, StatusMeta> = {
  critical: { label: 'Critical', variant: 'danger' },
  high: { label: 'High', variant: 'danger' },
  medium: { label: 'Medium', variant: 'warning' },
  low: { label: 'Low', variant: 'info' },
};

/** Severity display order — most urgent first. */
export const ALERT_SEVERITY_ORDER: AlertSeverity[] = ['critical', 'high', 'medium', 'low'];

export const ALERT_STATUS_META: Record<AlertStatus, StatusMeta> = {
  open: { label: 'Open', variant: 'secondary' },
  acknowledged: { label: 'Acknowledged', variant: 'info' },
  resolved: { label: 'Resolved', variant: 'success' },
  overridden: { label: 'Overridden', variant: 'warning' },
};

export const ALERT_CATEGORY_LABEL: Record<AlertCategory, string> = {
  compliance: 'Compliance',
  fraud: 'Fraud',
  attendance: 'Attendance',
  payroll: 'Payroll',
  data_quality: 'Data quality',
};

// --- Statutory Filings chips -------------------------------------------------

export const FILING_STATUS_META: Record<FilingStatus, StatusMeta> = {
  generated: { label: 'Generated', variant: 'info' },
  approved: { label: 'Approved', variant: 'purple' },
  submitted: { label: 'Submitted', variant: 'warning' },
  accepted: { label: 'Accepted', variant: 'teal' },
  rejected: { label: 'Rejected', variant: 'danger' },
  completed: { label: 'Completed', variant: 'success' },
  amended: { label: 'Amended', variant: 'secondary' },
};

/** Safe lookup — never returns undefined (guards against an unmapped status). */
export function filingStatusMeta(status: string): StatusMeta {
  return FILING_STATUS_META[status as FilingStatus] ?? { label: status || 'Unknown', variant: 'secondary' };
}

export const FILING_TYPE_LABEL: Record<FilingType, string> = {
  tra_paye: 'TRA PAYE Return',
  sdl: 'SDL Return',
  nssf: 'NSSF Contributions',
  wcf: 'WCF Contributions',
  heslb: 'HESLB Deductions',
  bank_payment: 'Bank payment file',
};

/** Display order of filing types within a legal-entity group. */
export const FILING_TYPE_ORDER: FilingType[] = ['tra_paye', 'sdl', 'nssf', 'wcf', 'heslb', 'bank_payment'];

export const COMPONENT_KIND_LABEL: Record<ComponentKind, string> = {
  earning: 'Earnings',
  pre_tax: 'Pre-tax deductions',
  tax: 'Statutory tax',
  post_tax: 'Post-tax deductions',
  employer: 'Employer contributions',
};

/** Display order of component groups in a payslip/line breakdown. */
export const COMPONENT_KIND_ORDER: ComponentKind[] = [
  'earning',
  'pre_tax',
  'tax',
  'post_tax',
  'employer',
];
